import React, { useEffect, useState } from 'react';
import { useFragment } from 'react-relay/hooks';

import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core';
import QuestionAnswerIcon from '@material-ui/icons/QuestionAnswer';
import { graphql } from 'babel-plugin-relay/macro';
import classnames from 'classnames';
import { get } from 'lodash';

import { PopperHubJr } from 'deprecated/query-triggers';

import { Badge, Fab, FabProps } from 'components/v2';

import { FabHubJr_Viewer$key } from './__generated__/FabHubJr_Viewer.graphql';

const styles = ({ spacing: { unit } }: Theme) =>
  createStyles({
    badge: {
      top: unit + 2,
      right: unit + 2,
    },
    // TODO move style to Badge component or wait until we upgrade it
    // https://material-ui.com/components/badges/
    badgeLargeDot: {
      height: 10,
      minWidth: 10,
    },
  });

// in case broadcast channel is not support uses local storage
const supportsBroadcastAPI = (() => window && (window as any).BroadcastChannel)();
const channel = supportsBroadcastAPI ? new BroadcastChannel('session-data') : null;

export interface FabHubJrProps extends Omit<FabProps, 'classes' | 'onClick'>, WithStyles<typeof styles> {
  fragmentRef: FabHubJr_Viewer$key;
}

const FabHubJr: React.FC<FabHubJrProps> = ({ classes, fragmentRef, ...rest }) => {
  const viewer = useFragment<FabHubJr_Viewer$key>(
    graphql`
      fragment FabHubJr_Viewer on User {
        countUnread: unreadChatsCount
        countUnassigned: chatActivityUnassignedCount
      }
    `,
    fragmentRef,
  );

  const [anchorEl, setAnchorEl] = useState<(EventTarget & HTMLElement) | null>(null);

  useEffect(() => {
    const triggerHubJuniorFromBroadcastChannel = (event: MessageEvent) => {
      if (get(event.currentTarget, 'name', '') === 'session-data') {
        if (get(event.data, 'openHubJunior', false)) {
          const button: HTMLButtonElement | null = document.querySelector('[data-lh-id="hubJr-fabButton"]');

          if (button && !anchorEl) button.click();
        }
      }
    };

    const triggerHubJuniorFromLocalStorage = (event: any) => {
      if (event.storageArea !== localStorage && event.key === 'session-data') {
        if (get(event, 'newValue.openHubJunior', false)) {
          const button: HTMLButtonElement | null = document.querySelector('#hubJr-fabButton');

          if (button && !anchorEl) button.click();
        }
      }
    };

    // listeners to validate user session through multiple window/tabs
    if (channel) {
      channel.onmessage = triggerHubJuniorFromBroadcastChannel;
    } else {
      window.addEventListener('storage', triggerHubJuniorFromLocalStorage);
    }
    return () => {
      if (!channel) {
        window.removeEventListener('storage', triggerHubJuniorFromLocalStorage);
      }
    };
  }, [anchorEl]);

  return (
    <>
      <Badge
        className={classnames(classes.badge, {
          [classes.badgeLargeDot]: !viewer.countUnread,
        })}
        color="secondary"
        variant={viewer.countUnread ? 'standard' : 'dot'}
        badgeContent={viewer.countUnread || viewer.countUnassigned}
      >
        <Fab
          color="supportPowder"
          aria-label="Chat"
          onClick={(event) => setAnchorEl((prevValue) => (prevValue ? null : event.currentTarget))}
          data-lh-id="hubJr-fabButton"
          {...rest}
        >
          <QuestionAnswerIcon />
        </Fab>
      </Badge>
      <PopperHubJr
        placement="bottom-end"
        modifiers={{
          flip: {
            enabled: false,
          },
          preventOverflow: {
            enabled: true,
            boundariesElement: 'scrollParent',
          },
        }}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
      />
    </>
  );
};

export default withStyles(styles)(FabHubJr);
